import React, { useEffect, useState } from "react";
import { FluidFormFields } from "../FormFields.component";
import { useFluidForm } from "../FormContext";
import { useRenderViewChange } from "../common";
import { SimpleFormClearButton } from "../SimpleFormClearButton";
export function InmersiveForm({ onSubmit }) {
    let configService = useFluidForm();
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const submitButtonLabel = configService.getAdditionalConfigValue("styleConfig", "submitButtonLabel") || "Submit";
    let currentFieldSection = configService.getCurrentFieldConfig()?.section;
    useRenderViewChange("inmersive");
    let cards = buildCards(configService);
    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'ArrowLeft' && currentCardIndex > 0) {
                event.preventDefault();
                handlePrevious();
            }
            else if (event.key === 'ArrowRight' && currentCardIndex < cards.length - 1) {
                event.preventDefault();
                handleNext();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentCardIndex, cards.length]);
    const handleSubmit = async () => {
        onSubmit?.();
    };
    const setCardIndex = (index) => {
        if (index >= cards.length || index < 0) {
            return;
        }
        let nextCard = cards[index];
        let nextField = nextCard.data.fieldPath;
        if (nextField) {
            configService.setCurrentField(nextField);
            setCurrentCardIndex(index);
        }
    };
    const handleNext = () => {
        setCardIndex(currentCardIndex + 1);
    };
    const handlePrevious = () => {
        setCardIndex(currentCardIndex - 1);
    };
    const handleCardClick = (index) => {
        setCardIndex(index);
    };
    //If currentField is different from the current card we need to calculate the right active card 
    useEffect(() => {
        if (configService.currentField) {
            // Find the index of the card that contains the current field
            const activeCardIndex = cards.findIndex(card => {
                // Check if the current field is one of the fields inside the card's containerField
                return card.data.fieldPath === configService.currentField;
            });
            // If we found a matching card, set it as active
            if (activeCardIndex !== -1 && activeCardIndex !== currentCardIndex) {
                setCurrentCardIndex(activeCardIndex);
            }
        }
    }, [configService.currentField, cards]);
    return (React.createElement("div", { className: "min-h-full f-bg-gradient flex flex-col" },
        React.createElement("div", { className: "w-full f-bg shadow-sm f-border-b" },
            React.createElement("div", { className: "max-w-4xl mx-auto px-6 py-4" },
                React.createElement("div", { className: "flex items-center justify-between mb-2" },
                    React.createElement("div", { className: "text-sm font-medium f-text" },
                        "Question ",
                        currentCardIndex + 1,
                        " of ",
                        cards.length),
                    React.createElement("div", { className: "text-sm f-text" },
                        Math.round(((currentCardIndex + 1) / cards.length) * 100),
                        "% complete")),
                React.createElement("div", { className: "w-full f-bg-dark rounded-full h-2" },
                    React.createElement("div", { className: "fluid-primary-bg h-2 rounded-full transition-all duration-500 ease-out", style: { width: `${((currentCardIndex + 1) / cards.length) * 100}%` } })))),
        React.createElement("div", { className: "flex-1 flex items-center justify-center px-6 py-12" },
            React.createElement("div", { className: "w-full max-w-2xl" },
                cards.length > 0 && (React.createElement("div", { className: "f-bg rounded-2xl shadow-xl border f-border overflow-visible" },
                    React.createElement("div", { className: "px-8 py-6 f-bg-gradient f-border-b rounded-t-2xl overflow-hidden" },
                        React.createElement("h2", { className: "text-2xl font-bold f-text leading-tight" }, cards[currentCardIndex].title || `Question ${currentCardIndex + 1}`),
                        React.createElement("div", { className: "mt-2 text-sm f-text-light" }, "Please provide your answer below")),
                    React.createElement("div", { className: "px-8 py-8" },
                        React.createElement(FluidFormFields, { field: cards[currentCardIndex].data, options: { omitLabel: true } })))),
                React.createElement("div", { className: "flex items-center justify-between mt-8" },
                    currentCardIndex > 0 ? (React.createElement("button", { onClick: handlePrevious, className: "flex items-center space-x-2 px-6 py-3 fluid-button-secondary" },
                        React.createElement("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
                            React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15 19l-7-7 7-7" })),
                        React.createElement("span", { className: "font-medium" }, "Previous"))) : (React.createElement("div", null)),
                    currentCardIndex < cards.length - 1 ? (React.createElement("button", { onClick: handleNext, className: "flex items-center space-x-2 px-8 py-3 fluid-button-primary" },
                        React.createElement("span", null, "Continue"),
                        React.createElement("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
                            React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 5l7 7-7 7" })))) : (React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(SimpleFormClearButton, null),
                        React.createElement("button", { onClick: handleSubmit, className: "flex items-center fluid-button-primary" },
                            React.createElement("span", null, submitButtonLabel),
                            React.createElement("svg", { className: "w-4 h-4 ml-2 fluid-icon", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
                                React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }))))))))));
}
function buildCards(configService) {
    let renderView = configService.getRenderView();
    let cards = [];
    let firstFocusableField = configService.getFirstFocusableField();
    let emptyRenderView = {
        ...renderView,
        fieldPath: firstFocusableField,
        value: {},
        cardSection: "root"
    };
    let currentCard = {
        data: { ...emptyRenderView },
        title: "",
        type: "section",
        containerField: { type: "section", key: "root" },
        cardSection: "root"
    };
    for (let [key, fieldRenderView] of Object.entries(renderView.value)) {
        let fieldConfig = configService.getConfig(fieldRenderView.configName);
        if (fieldConfig.type === "section") {
            continue;
        }
        cards.push({
            data: fieldRenderView,
            title: fieldConfig.label || "",
            type: "section",
            containerField: fieldConfig,
            cardSection: fieldConfig.key
        });
    }
    return cards;
}
